const NUMBER = String.raw `\d{1,3}(?:[.,]\d{1,2})?`;
const RANGE = String.raw `(${NUMBER})(?:\s?(?:-|–|bis|to)\s?(${NUMBER}))?`;
// "180 °C", "180°C", "180 Grad", "180 Grad Celsius", "180 degrees C", "350 F", "350°F", "350 degrees Fahrenheit"
const TEMPERATURE = new RegExp(String.raw `${RANGE}\s?(?:(°\s?C\b|° ?Celsius\b|Grad Celsius\b|degrees? (?:C\b|Celsius\b)|Grad\b(?! F))|(°\s?F\b|° ?Fahrenheit\b|Grad Fahrenheit\b|degrees? (?:F\b|Fahrenheit\b)|F\b(?=[\s.,;)]|$)))`, 'giu');
// "24 cm", "9 inch", "9-inch", "9 in.", '9"', "5 mm"
const LENGTH = new RegExp(String.raw `${RANGE}[\s-]?(?:(cm\b|mm\b|Zentimeter\b|centimet(?:er|re)s?\b|Millimeter\b)|(inch(?:es)?\b|in\.(?=\s|$)|Zoll\b|"(?=[\s.,;)]|$)))`, 'giu');
const parse = (raw) => Number(raw.replace(',', '.'));
function format(value, locale) {
    const text = Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
    return locale === 'de' ? text.replace('.', ',') : text;
}
/** Oven dials: 5 °C and 25 °F steps; below 100 °C / 200 °F (sugar, meat) to the degree. */
function celsiusToFahrenheit(c) {
    const f = (c * 9) / 5 + 32;
    return f >= 200 ? Math.round(f / 25) * 25 : Math.round(f);
}
function fahrenheitToCelsius(f) {
    const c = ((f - 32) * 5) / 9;
    return c >= 100 ? Math.round(c / 5) * 5 : Math.round(c);
}
function inchFraction(inches, locale) {
    const whole = Math.floor(inches);
    const quarters = Math.round((inches - whole) * 4);
    if (quarters === 4)
        return String(whole + 1);
    const glyph = ['', '¼', '½', '¾'][quarters];
    if (whole === 0)
        return glyph || format(Math.round(inches * 10) / 10, locale);
    return `${whole}${glyph}`;
}
function range(a, b, convert, unit) {
    const first = convert(parse(a));
    if (!b)
        return `${first} ${unit}`;
    const second = convert(parse(b));
    return first === second ? `${first} ${unit}` : `${first}–${second} ${unit}`;
}
/** The measurements a reader of `target` would want converted, in text order, without overlaps. */
export function findMeasurementAnnotations(text, target, locale = 'en') {
    const found = [];
    const wantsFahrenheit = target === 'US';
    for (const match of text.matchAll(TEMPERATURE)) {
        const [original, a, b, celsius, fahrenheit] = match;
        const start = match.index ?? 0;
        if (!a)
            continue;
        // "Grad" alone is Celsius in a German text; "350 F" needs a plausible oven value.
        if (fahrenheit && !wantsFahrenheit) {
            if (/^F$/i.test(fahrenheit.trim()) && parse(a) < 120)
                continue;
            found.push({
                start,
                end: start + original.length,
                original,
                converted: range(a, b, (v) => String(fahrenheitToCelsius(v)), '°C'),
                kind: 'TEMPERATURE',
            });
        }
        else if (celsius && wantsFahrenheit) {
            found.push({
                start,
                end: start + original.length,
                original,
                converted: range(a, b, (v) => String(celsiusToFahrenheit(v)), '°F'),
                kind: 'TEMPERATURE',
            });
        }
    }
    for (const match of text.matchAll(LENGTH)) {
        const [original, a, b, metric, imperial] = match;
        const start = match.index ?? 0;
        if (!a)
            continue;
        if (found.some((f) => start < f.end && start + original.length > f.start))
            continue;
        if (metric && target === 'US') {
            const factor = /^(mm|millimeter)/i.test(metric) ? 0.1 : 1;
            found.push({
                start,
                end: start + original.length,
                original,
                converted: range(a, b, (v) => inchFraction((v * factor) / 2.54, locale), 'inch'),
                kind: 'LENGTH',
            });
        }
        else if (imperial && target !== 'US') {
            found.push({
                start,
                end: start + original.length,
                original,
                converted: range(a, b, (v) => format(Math.round(v * 2.54 * 2) / 2, locale), 'cm'),
                kind: 'LENGTH',
            });
        }
    }
    return found.sort((x, y) => x.start - y.start);
}
/** The text with every conversion added in brackets behind its original. */
export function annotateMeasurements(text, target, locale = 'en') {
    const annotations = findMeasurementAnnotations(text, target, locale);
    if (annotations.length === 0)
        return text;
    let out = '';
    let cursor = 0;
    for (const a of annotations) {
        // Already annotated by the author: "180 °C (350 °F)" stays as it is.
        const following = text.slice(a.end, a.end + 24);
        const alreadyThere = /^\s?[(/]\s?\d/.test(following);
        out += text.slice(cursor, a.end);
        if (!alreadyThere)
            out += ` (${a.converted})`;
        cursor = a.end;
    }
    return out + text.slice(cursor);
}
//# sourceMappingURL=annotate-measurements.js.map